import {
  getDeviceType,
  trackPurchaseStripePay,
  trackMSPurchaseSuccess,
} from 'helpers';
import moment from 'moment';
import { Action } from 'redux';
import {
  createProvidentPaymentIntent,
  verifyProvidentPaymentIntent,
} from 'services';
import { IFullAddress, IPolicyProvider, IPolicyScan, IQuote } from 'types';
import { FREQUENCY } from 'constants/options';
import { GetUserPolicies } from './policy';
import { GetNotifications } from './notification-actions';
import { ErrorToast } from 'components/toasts';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';

export enum Types {
  SET_PAYMENT_STATUS = 'SET_PAYMENT_STATUS',
  SET_SHOW_STRIPE = 'SET_SHOW_STRIPE',
  SET_STRIPE_LOADING = 'SET_STRIPE_LOADING',
}

export interface ISetPaymentStatus extends Action {
  type: Types.SET_PAYMENT_STATUS;
  data: string;
}

export const SetPaymentStatus = (data: string) =>
  ({
    type: Types.SET_PAYMENT_STATUS,
    data,
  }) as ISetPaymentStatus;

export interface ISetShowStripe extends Action {
  type: Types.SET_SHOW_STRIPE;
  data: boolean;
}

export const SetShowStripe = (data: boolean) =>
  ({
    type: Types.SET_SHOW_STRIPE,
    data,
  }) as ISetShowStripe;

export interface ISetStripeLoading extends Action {
  type: Types.SET_STRIPE_LOADING;
  data: boolean;
}

export const SetStripeLoading = (data: boolean) =>
  ({
    type: Types.SET_STRIPE_LOADING,
    data,
  }) as ISetStripeLoading;

export const HandleProvidentStripePayment =
  (
    provider: IPolicyProvider,
    policyScan: IPolicyScan,
    paymentType: FREQUENCY,
    quote: { yearly: number; monthly: number; fortnightly?: number },
    form: any,
    providentQuote: IQuote,
    stripe: Stripe,
    cardElement: StripeCardElement,
    showPaymentSuccessPage: (data: any) => void,
    experimentValue: string,
  ) =>
    async (dispatch: any) => {
      const billingDetails = {
        name: `${form.values.firstName} ${form.values.lastName}`,
        email: `${form.values.email}`,
      };

      dispatch(SetStripeLoading(true));

      trackPurchaseStripePay({
        policyProviderName: provider.name,
        status: 'pending',
        experimentValue: experimentValue || '',
        paymentFrequency: paymentType,
      });

      dispatch(SetPaymentStatus('Processing your payment securely...'));

      const fullAddress = policyScan.fullAddress as IFullAddress;

      // The API ignores body.amount and re-prices from the post-issue quote,
      // but we still send the frequency-appropriate first charge for parity.
      // Fortnightly may be absent on older quotes — fall back to monthly.
      const premium = quote[paymentType.toLowerCase()] ?? quote.monthly;

      const { data } = await createProvidentPaymentIntent({
        amount: premium,
        address: form.values.overnightParkingAddress,
        quoteRef: form.values.quoteRef,
        paymentType: form.values.paymentType,
        policyScanId: policyScan.id,
        excess: providentQuote?.standardExcess || form.values.excess,
        voluntaryExcess: form.values.voluntaryExcess,
        insuredAmount: form.values.insuredAmount,
        city: fullAddress.city,
        streetLine1: `${fullAddress.streetNo} ${fullAddress.streetName}`,
        postCode: fullAddress.postCode,
        policyStartDate: moment(form.values.policyStartDate).format('YYYY-MM-DD'),
      });

      dispatch(
        SetPaymentStatus(`Almost there! We're generating your policy details...`),
      );

      const confirmPaymentResult = await stripe.confirmCardPayment(
        data.paymentId,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        },
      );

      if (confirmPaymentResult.error) {
        trackPurchaseStripePay({
          policyProviderName: provider.name,
          status: 'failed',
          paymentFrequency: paymentType,
        });
        dispatch(SetStripeLoading(false));
        dispatch(SetPaymentStatus(``));
        ErrorToast.error({
          headerMessage: 'Something went wrong.',
          message:
            'Your payment has been declined. Please check your card details and try again.',
        });
      } else {
        dispatch(
          SetPaymentStatus(
            `Just a moment longer... We're finalising everything for you.`,
          ),
        );
        try {
          const policyData = (
            await verifyProvidentPaymentIntent({
              paymentIntent: confirmPaymentResult.paymentIntent,
              policyId: policyScan.sk,
              policyScanId: policyScan.id,
              purchaseSessionId: data.purchaseSessionId,
              quoteRef: form.values.quoteRef,
              underwritingOutcomeResponseId:
                data.underwritingOutcomeResponseIdToUse,
              optionId: data.optionIdToUse,
              deviceType: getDeviceType(),
            })
          ).data;
          trackMSPurchaseSuccess({
            id: policyScan.id,
            providerName: provider.name,
            experimentValue: experimentValue || '',
            paymentFrequency: paymentType,
          });
          dispatch(GetUserPolicies());
          dispatch(GetNotifications(true));

          showPaymentSuccessPage(policyData);
          dispatch(SetStripeLoading(false));
          dispatch(SetPaymentStatus(``));
        } catch (e) {
          trackPurchaseStripePay({
            policyProviderName: provider.name,
            status: 'failed',
            paymentFrequency: paymentType,
          });
          dispatch(SetStripeLoading(false));
          dispatch(SetPaymentStatus(``));
          ErrorToast.error({
            headerMessage: 'Something went wrong.',
            message:
              'We apologize for the interruption. Please try again. If the issue continues, contact our support team at team@quashed.co.nz for assistance.',
          });
        }
      }
    };
